import { usePreventRemove } from '@react-navigation/native';
import { Stack, useFocusEffect, useNavigation, useRouter } from 'expo-router';
import { useCallback, useRef } from 'react';
import { StyleSheet, View } from 'react-native';

import {
  Button,
  HeaderFrame,
  HeaderIconButton,
  IconChevronLeft,
  IconClose,
  ScrollView,
  Text,
  YStack,
  createMqStyles,
  tokens,
  useMqSelect,
} from '@fhs/ui';
export { ErrorBoundary } from '@fhs/routing/src/components/page-dev-error-boundary';

import { CustomizationGroup } from '../components/customization-group';
import { useConfirmUnsavedCustomizationModal } from '../components/use-confirm-unsaved-customization-modal';
import { useMenuPicker } from '../state/menu-picker';

const listItemProps = { titleFontWeight: 'normal' } as const;

export function ScreenCustomizeItem() {
  const router = useRouter();
  const menuPicker = useMenuPicker();

  const mqStyles = useMqStyles();
  const isDesktop = useMqSelect({ $gteDesktop: true }, false);

  const {
    setCustomizationSelections,
    draftCustomizationSelections,
    hasUnsavedCustomizationSelections,
  } = menuPicker;

  const handleSave = useCallback(() => {
    isBackAlreadyHandled.current = true;
    setCustomizationSelections(draftCustomizationSelections);
    router.dismiss(1);
  }, [setCustomizationSelections, draftCustomizationSelections, router]);
  const isBackAlreadyHandled = useRef(false);

  const { confirmationDialog, openConfirmationDialog } = useConfirmUnsavedCustomizationModal({
    onOpen: useCallback(() => {
      isBackAlreadyHandled.current = true;
    }, []),
    onClose: useCallback(() => {
      router.dismiss(1);
    }, [router]),
  });

  const handleClose = () => {
    isBackAlreadyHandled.current = true;
    if (hasUnsavedCustomizationSelections) {
      openConfirmationDialog();
    } else {
      router.dismiss(1);
    }
  };

  const displayGroups =
    menuPicker.selectedPickerAspectOption?.item?.customizations.displayGroups ?? [];

  const allCustomizations = displayGroups.filter(
    customization => !customization.displayGroup.showOnProductPage
  );

  const navigation = useNavigation();
  usePreventRemove(hasUnsavedCustomizationSelections, ({ data }) => {
    if (isBackAlreadyHandled.current) {
      navigation.dispatch(data.action);
    } else {
      openConfirmationDialog();
    }
  });

  useFocusEffect(
    useCallback(() => {
      isBackAlreadyHandled.current = false;
    }, [])
  );

  return (
    <View style={styles.screen}>
      <Stack.Screen
        options={{
          header: () => (
            <HeaderFrame style={mqStyles.headerFrame}>
              {!isDesktop && (
                <HeaderFrame.Left>
                  <HeaderIconButton onPress={handleClose}>
                    <IconChevronLeft />
                  </HeaderIconButton>
                </HeaderFrame.Left>
              )}
              <HeaderFrame.Middle>
                <YStack style={mqStyles.headerTitleStack}>
                  <Text.Heading type={isDesktop ? 'one' : 'four'}>Customize Item</Text.Heading>
                  <Text.Paragraph size={isDesktop ? 'md' : 'sm'}>
                    {[
                      menuPicker.selectedPickerAspectOption?.displayName,
                      menuPicker.selectedPickerAspectOption?.item?.baseCalories &&
                        `${menuPicker.selectedPickerAspectOption?.item?.baseCalories} Cal`,
                    ]
                      .filter(Boolean)
                      .join(' • ')}
                  </Text.Paragraph>
                </YStack>
              </HeaderFrame.Middle>
              {isDesktop && (
                <Button
                  type="outline-subtle"
                  size="xl"
                  onPress={handleClose}
                  style={styles.desktopCloseButton}
                >
                  <Button.Icon>
                    <IconClose />
                  </Button.Icon>
                </Button>
              )}
            </HeaderFrame>
          ),
        }}
      />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent}>
        {allCustomizations.map((customizationGroup, i) => (
          <CustomizationGroup
            borderless
            listItemProps={listItemProps}
            customizationGroup={customizationGroup}
            key={customizationGroup.displayGroup.id ?? i}
            customizationSelections={menuPicker.draftCustomizationSelections}
            setCustomizationSelections={menuPicker.setDraftCustomizationSelections}
          />
        ))}
      </ScrollView>
      <View style={styles.screenFooter}>
        <View style={styles.screenFooterContent}>
          <Button size="xl" onPress={handleSave}>
            <Button.Text>Looks Good!</Button.Text>
          </Button>
        </View>
      </View>
      {confirmationDialog}
    </View>
  );
}

const MAX_CONTENT_WIDTH = 480;

const useMqStyles = createMqStyles({
  headerFrame: {
    $gteDesktop: {
      minHeight: 132,
    },
  },
  headerTitleStack: {
    $base: {
      backgroundColor: tokens.colors.$white,
      alignItems: 'center',
      gap: 4,
    },
    $gteDesktop: {
      paddingVertical: 16,
    },
  },
});

const styles = StyleSheet.create({
  screen: {
    flexGrow: 1,
    flexShrink: 1,
    overflow: 'hidden',
    width: '100%',
  },
  scrollView: {
    flexShrink: 1,
    flexGrow: 1,
    width: '100%',
    backgroundColor: tokens.colors.$white,
  },
  scrollViewContent: {
    marginHorizontal: 'auto',
    width: '100%',
    maxWidth: MAX_CONTENT_WIDTH,
    padding: 16,
    gap: 24,
  },
  screenFooter: {
    backgroundColor: tokens.colors.$white,
    borderTopColor: tokens.colors.$black10,
    borderTopWidth: 1,
  },
  screenFooterContent: {
    padding: 16,
    width: '100%',
    maxWidth: MAX_CONTENT_WIDTH,
    marginHorizontal: 'auto',
  },
  headerTitleStack: {
    alignItems: 'center',
  },
  desktopCloseButton: {
    position: 'absolute',
    top: 20,
    right: 32,
  },
});
